import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Calculator, HelpCircle, Info, CheckCircle2, RefreshCw, ArrowRight, ChevronRight, Square, Circle, Triangle, Shapes } from 'lucide-react';
import { MaterialCalculator } from '../components/MaterialCalculator';
import { getProjects } from '../data/projects';
// The quiz resolves its recommendation to a project guide, so it still needs
// the project list even though the guide GRID now lives only on /projects.
const allProjects = getProjects();
// Shape options for the calculator — each maps to a distinct area formula.
// 'irregular' uses the same length × width as rectangle so users can
// estimate odd shapes via their bounding box.
type Shape = 'rectangle' | 'circle' | 'triangle' | 'irregular';
const shapeOptions: {
  id: Shape;
  label: string;
  Icon: (props: {
    className?: string;
  }) => JSX.Element;
}[] = [{
  id: 'rectangle',
  label: 'Rectangle',
  Icon: Square
}, {
  id: 'circle',
  label: 'Circle',
  Icon: Circle
}, {
  id: 'triangle',
  label: 'Triangle',
  Icon: Triangle
}, {
  id: 'irregular',
  label: 'Irregular',
  Icon: Shapes
}];
// --- CALCULATOR DATA ---
const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french_drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
const calcMaterials = [{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$65'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$65'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$61'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$38'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$38'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$24'
}, {
  id: 'utility-sand',
  name: 'Utility Sand',
  density: 1.5,
  priceRange: '$52'
}, {
  id: 'gravel-borrow',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$48'
}, {
  id: '58-clear',
  name: '5/8" Crushed Clear',
  density: 1.35,
  priceRange: '$74'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: '125-clear',
  name: '1.25" Crushed Clear',
  density: 1.35,
  priceRange: '$68'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$72'
}, {
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$48'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$57'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$66'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$50'
}, {
  id: 'play-chips',
  name: 'Play Chips',
  density: 0.45,
  priceRange: '$55'
}, {
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$61'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$84'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$93'
}, {
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$82'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$85'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$72'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$152'
}, {
  id: 'concrete-sand',
  name: 'Concrete Sand',
  density: 1.35,
  priceRange: '$68'
}, {
  id: 'pit-run',
  name: 'Pit Run',
  density: 1.43,
  priceRange: '$37'
}, {
  id: 'hog-fuel',
  name: 'Hog Fuel',
  density: 0.4,
  priceRange: '$48'
}];
// --- QUIZ DATA ---
const quizQuestions = [{
  id: 'project',
  question: 'What type of project are you working on?',
  options: [{
    label: 'Driveway or Parking Area',
    value: 'driveway'
  }, {
    label: 'Drainage (French drain, retaining wall)',
    value: 'drainage'
  }, {
    label: 'Landscaping or Garden Beds',
    value: 'landscape'
  }, {
    label: 'Walkway or Patio Base',
    value: 'walkway'
  }, {
    label: 'Erosion Control or Slope Stabilization',
    value: 'erosion'
  }, {
    label: 'Concrete, Masonry, or Leveling',
    value: 'concrete'
  }, {
    label: 'Grading, Fill, or Raising Low Spots',
    value: 'fill'
  }, {
    label: 'Mud Control (paddock, trail, or soft ground)',
    value: 'mud'
  }]
}, {
  id: 'driveway_layer',
  dependsOn: {
    questionId: 'project',
    value: 'driveway'
  },
  question: 'Which layer are you building?',
  options: [{
    label: 'The deep foundation (Base layer)',
    value: 'base',
    result: {
      name: '1.25" Minus',
      desc: 'A coarser crushed rock that compacts into a rock-solid foundation. Often used as the first layer before topping with finer gravel.'
    }
  }, {
    label: 'The top driving surface (Top layer)',
    value: 'top',
    result: {
      name: '5/8" Minus',
      desc: 'Our most popular driveway gravel — packs down tight and creates a smooth, firm driving surface.'
    }
  }, {
    label: 'Budget-friendly option (Recycled material)',
    value: 'budget',
    result: {
      name: 'Recycled Concrete or Recycled Asphalt',
      desc: 'Sustainable, affordable alternatives that perform like virgin rock. Recycled Asphalt even re-binds over time for a semi-hard surface. Great for rural driveways and parking areas.'
    }
  }]
}, {
  id: 'drainage_type',
  dependsOn: {
    questionId: 'project',
    value: 'drainage'
  },
  question: 'What kind of drainage?',
  options: [{
    label: 'Standard French Drain',
    value: 'french',
    result: {
      name: '7/8" Drain Rock',
      desc: 'The Puget Sound standard for residential French drains. Washed clean so water flows freely through it.'
    }
  }, {
    label: 'Heavy-duty / Behind Retaining Wall',
    value: 'heavy',
    result: {
      name: '1.5" Drain Rock or 1.25" Crushed Clear',
      desc: 'Larger stone with bigger voids for maximum water flow in heavy-duty drainage and retaining wall backfill.'
    }
  }]
}, {
  id: 'landscape_type',
  dependsOn: {
    questionId: 'project',
    value: 'landscape'
  },
  question: 'What are you trying to achieve?',
  options: [{
    label: 'Planting grass or new plants',
    value: 'plants',
    result: {
      name: '3-Way Topsoil',
      desc: 'A premium blend of topsoil, compost, and sand — everything your plants need to thrive from day one.'
    }
  }, {
    label: 'Amending heavy clay soil',
    value: 'amend',
    result: {
      name: '50/50 Mix',
      desc: 'Half topsoil, half compost. Packed with organic matter to break up clay and boost soil health.'
    }
  }, {
    label: 'Pure compost for enriching soil',
    value: 'compost',
    result: {
      name: 'Compost',
      desc: 'Rich, fully aged organic compost packed with beneficial microbes and nutrients. Top-dress lawns, enrich raised beds, or boost vegetable garden health.'
    }
  }, {
    label: 'Weed control and moisture retention',
    value: 'weeds',
    result: {
      name: 'Fine, Medium, or Coarse Bark',
      desc: 'Natural PNW bark mulch to protect beds, suppress weeds, and retain moisture. Fine Bark for a polished look, Medium for all-around use, Coarse for maximum longevity.'
    }
  }, {
    label: 'Decorative ground cover or dry creek bed',
    value: 'decorative',
    result: {
      name: 'Streambed Cobble, River Rock, or Lava Rock',
      desc: 'Streambed Cobble for a natural creek bed look, River Rock for smooth organic accents, or Lava Rock for bold reddish-brown color and lightweight coverage. All zero-maintenance.'
    }
  }, {
    label: 'Decorative walking path',
    value: 'path',
    result: {
      name: 'Pea Gravel',
      desc: 'Small, smooth rounded stones for a beautiful, crunching pathway that drains well.'
    }
  }]
}, {
  id: 'walkway_type',
  dependsOn: {
    questionId: 'project',
    value: 'walkway'
  },
  question: 'What is the surface going to be?',
  options: [{
    label: 'Pavers or Flagstone (Need a base)',
    value: 'pavers',
    result: {
      name: '5/8" Minus + Utility Sand',
      desc: 'Use 5/8" Minus as a compacted sub-base, then a layer of Utility Sand to level and set your pavers.'
    }
  }, {
    label: 'Just gravel (Walking on it directly)',
    value: 'gravel',
    result: {
      name: 'Pea Gravel or 5/8" Minus',
      desc: 'Pea gravel for a loose decorative feel, or 5/8" Minus for a firm, packed path.'
    }
  }]
}, {
  id: 'erosion_type',
  dependsOn: {
    questionId: 'project',
    value: 'erosion'
  },
  question: 'What does the area look like?',
  options: [{
    label: 'Steep slope or embankment',
    value: 'steep',
    result: {
      name: '2-4" Quarry Spalls or Large Rocks',
      desc: 'Quarry Spalls (2-4") lock in place on slopes for standard rip-rap. For extreme erosion or shoreline protection, Large Rocks (1-4 man) provide maximum mass and stability — nothing moves them.'
    }
  }, {
    label: 'Gentle slope or ditch lining',
    value: 'gentle',
    result: {
      name: '1.25" Crushed Clear, Streambed Cobble, or 2-4" Quarry Spalls',
      desc: 'For gentle slopes, 1.25" Crushed Clear provides drainage and stability. Streambed Cobble offers a natural, decorative look while controlling erosion. For steeper ditches, step up to 2-4" Quarry Spalls.'
    }
  }, {
    label: 'Severe erosion or shoreline armoring',
    value: 'severe',
    result: {
      name: 'Large Rocks (1-4 Man)',
      desc: 'Massive quarry rocks ranging from 100 lbs to 2,000+ lbs. The ultimate solution for shoreline protection, steep bank armoring, and heavy rip-rap where nothing else will hold. Delivery includes placement.'
    }
  }]
}, {
  id: 'concrete_type',
  dependsOn: {
    questionId: 'project',
    value: 'concrete'
  },
  question: 'What are you doing?',
  options: [{
    label: 'Mixing concrete or mortar',
    value: 'mixing',
    result: {
      name: 'Concrete Sand',
      desc: 'Washed, coarse sand graded to concrete-mix spec (ASTM C33). Sharper and cleaner than utility sand, so your mix is consistent batch to batch.'
    }
  }, {
    label: 'Leveling under pavers, a pool, or a shed',
    value: 'leveling',
    result: {
      name: 'Utility Sand',
      desc: 'Clean, coarse sand that spreads and levels easily. Perfect as a setting bed under pavers, pools, and structures.'
    }
  }, {
    label: 'Bedding pipe or a septic drainfield',
    value: 'bedding',
    result: {
      name: 'Concrete Sand',
      desc: 'The washed grading keeps water moving instead of holding it, which is exactly what pipe bedding and septic drainfields need.'
    }
  }]
}, {
  id: 'fill_type',
  dependsOn: {
    questionId: 'project',
    value: 'fill'
  },
  question: 'What do you need to accomplish?',
  options: [{
    label: "Raise grade or fill low spots (won't be planting)",
    value: 'grade',
    result: {
      name: 'Fill Dirt',
      desc: 'Our most affordable bulk material. Clean subsoil for raising grades, backfilling, and establishing drainage slopes.'
    }
  }, {
    label: 'Fill and then plant on top',
    value: 'plant',
    result: {
      name: 'Fill Dirt + 3-Way Topsoil',
      desc: 'Use Fill Dirt to build up the area, then top with 4-6 inches of 3-Way Topsoil for a plantable surface.'
    }
  }, {
    label: 'Affordable bulk fill for subgrade or earthwork',
    value: 'bulk',
    result: {
      name: 'Gravel Borrow',
      desc: 'A naturally occurring pit-run gravel blend that compacts well. Meets WSDOT Type 17 specs — great for building up subgrade, backfill, and general earthwork at a lower cost than crushed rock.'
    }
  }, {
    label: 'Build up soft, wet ground that will not hold a truck',
    value: 'wet',
    result: {
      name: 'Pit Run',
      desc: 'Screened bank-run fill that compacts tight for a fraction of the cost of crushed rock. The standard way to get a stable platform out of mud before you place anything structural on top.'
    }
  }]
}, {
  id: 'mud_type',
  dependsOn: {
    questionId: 'project',
    value: 'mud'
  },
  question: 'What is the area used for?',
  options: [{
    label: 'Horse paddock, arena, trail, or dog run',
    value: 'organic',
    result: {
      name: 'Hog Fuel',
      desc: 'Coarsely ground wood and bark that mats together into a spongy surface. The Puget Sound standard for keeping hooves and boots out of the mud once the fall rains start — and a fraction of the cost of gravel for the same coverage.'
    }
  }, {
    label: 'Driveway or parking area that has gone soft',
    value: 'driving',
    result: {
      name: 'Pit Run + 5/8" Minus',
      desc: 'Place pit run to build a stable platform out of the mud, then cap it with 5/8" Minus for a firm driving surface. Skipping the pit run on saturated ground just buries good rock.'
    }
  }, {
    label: 'Construction site access or equipment staging',
    value: 'site',
    result: {
      name: '4-8" Spalls or Pit Run',
      desc: '4-8" Spalls make a proper construction entrance that knocks mud off tires. For general soft ground you need to drive on, pit run is the cheaper way to get a working platform.'
    }
  }]
}];
// Recommended depth per quiz ANSWER, keyed `${questionId}:${optionValue}`.
// The project type alone can't express this: topping off an existing driveway
// needs 2–3", while building a new base under one needs 4–6". Opening the
// calculator at a blanket 4" quietly over-ordered on every refresh job.
// `projectType` also seeds the calculator's dropdown so the two agree.
const QUIZ_DEPTH_GUIDE: Record<string, {
  depth: number;
  projectType: string;
  note: string;
}> = {
  'driveway_layer:base': {
    depth: 5,
    projectType: 'driveway',
    note: 'A new driveway base is normally 4–6" compacted. Set to 5" — go deeper for soft ground or truck traffic.'
  },
  'driveway_layer:top': {
    depth: 3,
    projectType: 'driveway',
    note: 'A top driving surface only needs 2–3" over a solid base. Set to 3" — drop to 2" if you\'re just topping off.'
  },
  'driveway_layer:budget': {
    depth: 4,
    projectType: 'driveway',
    note: 'Recycled material is usually placed 3–4" deep over compacted ground.'
  },
  'drainage_type:french': {
    depth: 12,
    projectType: 'french-drain',
    note: 'A standard French drain trench is about 12" of rock around the pipe.'
  },
  'drainage_type:heavy': {
    depth: 12,
    projectType: 'french-drain',
    note: 'Heavy-duty and retaining-wall backfill runs 12" or more behind the wall.'
  },
  'landscape_type:plants': {
    depth: 4,
    projectType: 'fill',
    note: 'New lawn or planting beds need 4" of topsoil for roots to establish. Use 6" for vegetable beds.'
  },
  'landscape_type:amend': {
    depth: 3,
    projectType: 'fill',
    note: 'Spread 2–3" over clay, then till it in rather than leaving it on top.'
  },
  'landscape_type:compost': {
    depth: 2,
    projectType: 'fill',
    note: 'Top-dressing with compost is 1–2" — more than that can smother a lawn.'
  },
  'landscape_type:weeds': {
    depth: 3,
    projectType: 'fill',
    note: 'Bark mulch is installed 2–3" deep. Thinner won\'t hold back weeds; thicker can hold too much moisture at the stems.'
  },
  'landscape_type:decorative': {
    depth: 3,
    projectType: 'fill',
    note: 'Decorative rock reads best at 2–3" — enough to fully cover the ground beneath.'
  },
  'landscape_type:path': {
    depth: 3,
    projectType: 'walkway',
    note: 'A pea gravel path is 2–3" deep. Any deeper and it feels loose underfoot.'
  },
  'walkway_type:pavers': {
    depth: 4,
    projectType: 'patio',
    note: 'Paver sub-base runs about 4" compacted, plus roughly 1" of sand for setting.'
  },
  'walkway_type:gravel': {
    depth: 3,
    projectType: 'walkway',
    note: 'A walk-on gravel path only needs 2–3".'
  },
  'erosion_type:steep': {
    depth: 12,
    projectType: 'fill',
    note: 'Rip-rap on a slope is placed roughly 12" thick so the stone locks together.'
  },
  'erosion_type:gentle': {
    depth: 6,
    projectType: 'fill',
    note: 'Ditch lining and gentle slopes hold at 4–6".'
  },
  'erosion_type:severe': {
    depth: 18,
    projectType: 'fill',
    note: 'Shoreline armoring runs 18" or more. Call dispatch — placement of rock this size is quoted per site.'
  },
  'concrete_type:mixing': {
    depth: 4,
    projectType: 'fill',
    note: 'For mix sand, enter the volume you need rather than a spread depth.'
  },
  'concrete_type:leveling': {
    depth: 1,
    projectType: 'patio',
    note: 'A setting bed under pavers is about 1". Under a pool or shed, 2".'
  },
  'concrete_type:bedding': {
    depth: 6,
    projectType: 'fill',
    note: 'Pipe bedding is typically 6" under and around the pipe.'
  },
  'fill_type:grade': {
    depth: 6,
    projectType: 'fill',
    note: 'Set this to how much you actually need to raise the grade — 6" is only a starting point.'
  },
  'fill_type:plant': {
    depth: 6,
    projectType: 'fill',
    note: 'Calculate the fill depth here, then run it again at 4–6" for the topsoil layer on top.'
  },
  'fill_type:bulk': {
    depth: 6,
    projectType: 'fill',
    note: 'Gravel borrow is placed in 6" lifts and compacted between each one.'
  },
  'fill_type:wet': {
    depth: 8,
    projectType: 'fill',
    note: 'Building out of mud usually takes 8–12". Skimping here just sinks the rock.'
  },
  'mud_type:organic': {
    depth: 6,
    projectType: 'fill',
    note: 'Hog fuel is placed 4–6" deep, and it settles — plan for the deeper end in high-traffic areas.'
  },
  'mud_type:driving': {
    depth: 6,
    projectType: 'driveway',
    note: 'Figure 6" of pit run to build the platform, then run this again at 3" for the 5/8" Minus cap.'
  },
  'mud_type:site': {
    depth: 8,
    projectType: 'fill',
    note: 'A construction entrance is built 8–12" thick so it knocks mud off tires.'
  }
};
// Project guide that covers a given quiz answer, keyed the same way as
// QUIZ_DEPTH_GUIDE. The guide carries the full layer stack, depths, and
// build steps — more than a single material recommendation can, so where one
// exists it's the better next step than the calculator alone.
const QUIZ_PROJECT_GUIDE: Record<string, string> = {
  'driveway_layer:base': 'new-gravel-driveway',
  'driveway_layer:top': 'driveway-top-off',
  'driveway_layer:budget': 'new-gravel-driveway',
  'drainage_type:french': 'french-drain',
  'drainage_type:heavy': 'retaining-wall-backfill',
  'landscape_type:weeds': 'beauty-bark-refresh',
  'landscape_type:path': 'pea-gravel-path',
  'walkway_type:pavers': 'paver-patio-base',
  'walkway_type:gravel': 'pea-gravel-path',
  'fill_type:wet': 'muddy-driveway-fix',
  'mud_type:driving': 'muddy-driveway-fix'
};
// Map a quiz result name (which may contain alternatives like
// "Recycled Concrete or Recycled Asphalt" or "Fine, Medium, or Coarse Bark")
// to the first matching calcMaterials.id. Falls back to first material if
// no match is found.
function quizResultToMaterialId(resultName: string): string {
  if (!resultName) return calcMaterials[0].id;
  // Try exact match first
  const exact = calcMaterials.find(m => m.name === resultName);
  if (exact) return exact.id;
  // Then substring match — pick the first material whose name appears in
  // the result string. Sort by name length descending so longer names
  // (e.g. "1.25\" Minus") win over short prefixes (e.g. "1.25\" Clean").
  const sorted = [...calcMaterials].sort((a, b) => b.name.length - a.name.length);
  for (const m of sorted) {
    if (resultName.toLowerCase().includes(m.name.toLowerCase())) return m.id;
  }
  return calcMaterials[0].id;
}
export function HelpMeChoose() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Return-flow params: when the user came from the estimator or a product
  // page, we surface an "Apply X yards" button on the calculator result so
  // they can hand the calculated yardage back to where they started.
  const fromParam = searchParams.get('from'); // 'estimator' | 'product' | null
  const fromSlug = searchParams.get('slug');
  const fromAddress = searchParams.get('address');
  // Pull deep-link params (from Shop by Project page) up front so initial
  // state is correct on first render — no flash of default values.
  const initialProjectParam = searchParams.get('project');
  const initialMaterialParam = searchParams.get('material');
  const hasDeepLinkParams = Boolean(initialProjectParam || initialMaterialParam);
  const initialProjectType = initialProjectParam && projectTypes.some(p => p.id === initialProjectParam) ? initialProjectParam : projectTypes[0].id;
  // Look up the FULL project definition so the calculator can seed its
  // initial depth state with the project's default depth. Falls back to
  // the first projectType if nothing matches (shouldn't happen because
  // initialProjectType is always a valid id, but defensive anyway).
  const initialProjectDef = projectTypes.find(p => p.id === initialProjectType) ?? projectTypes[0];
  const initialMaterial = initialMaterialParam && calcMaterials.some(m => m.id === initialMaterialParam) ? initialMaterialParam : calcMaterials[0].id;
  const [activeTab, setActiveTab] = useState<'calculator' | 'quiz'>(searchParams.get('tab') === 'calculator' || hasDeepLinkParams ? 'calculator' : 'quiz');
  const [quizMaterial, setQuizMaterial] = useState(initialMaterial);
  // Calculator State
  const [projectType, setProjectType] = useState(initialProjectType);
  const [shape, setShape] = useState<Shape>('rectangle');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState(initialProjectDef.defaultDepth.toString());
  const [material, setMaterial] = useState(initialMaterial);
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  useEffect(() => {
    const selectedProject = projectTypes.find(p => p.id === projectType);
    if (selectedProject) setDepth(selectedProject.defaultDepth.toString());
  }, [projectType]);
  // Recalculate whenever shape changes so the user doesn't see stale numbers
  // from a previous shape's dimensions.
  useEffect(() => {
    setCalcResults(null);
  }, [shape]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const d = parseFloat(depth);
    if (isNaN(d) || d <= 0) return;
    let areaSqFt = 0;
    if (shape === 'rectangle' || shape === 'irregular') {
      const l = parseFloat(length);
      const w = parseFloat(width);
      if (isNaN(l) || isNaN(w)) return;
      areaSqFt = l * w;
    } else if (shape === 'circle') {
      const dia = parseFloat(diameter);
      if (isNaN(dia)) return;
      const r = dia / 2;
      areaSqFt = Math.PI * r * r;
    } else if (shape === 'triangle') {
      const b = parseFloat(base);
      const h = parseFloat(height);
      if (isNaN(b) || isNaN(h)) return;
      areaSqFt = 0.5 * b * h;
    }
    const depthInFeet = d / 12;
    const cubicFeet = areaSqFt * depthInFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  // Quiz State
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<{
    name: string;
    desc: string;
  } | null>(null);
  // Depth + project type implied by the answer that produced the result, so
  // the calculator opens on the right depth instead of a blanket 4".
  const [quizDepthGuide, setQuizDepthGuide] = useState<{
    depth: number;
    projectType: string;
    note: string;
  } | null>(null);
  // Slug of the project guide covering the answered job, when one exists.
  const [quizGuideSlug, setQuizGuideSlug] = useState<string | null>(null);
  const quizGuide = quizGuideSlug ? allProjects.find(p => p.slug === quizGuideSlug) ?? null : null;
  const currentQuizQuestion = quizQuestions.find(q => {
    if (!q.dependsOn) return !quizAnswers[q.id];
    return quizAnswers[q.dependsOn.questionId] === q.dependsOn.value && !quizAnswers[q.id];
  });
  // Calculate progress
  const totalSteps = 2; // Most paths are 2 steps
  const currentStep = quizResult ? 3 : Object.keys(quizAnswers).length + 1;
  const progressPercent = Math.min(currentStep / (totalSteps + 1) * 100, 100);
  const handleQuizAnswer = (questionId: string, value: string, result?: {
    name: string;
    desc: string;
  }) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (result) {
      setQuizResult(result);
      setQuizDepthGuide(QUIZ_DEPTH_GUIDE[`${questionId}:${value}`] ?? null);
      setQuizGuideSlug(QUIZ_PROJECT_GUIDE[`${questionId}:${value}`] ?? null);
    }
  };
  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
    setQuizDepthGuide(null);
    setQuizGuideSlug(null);
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const inputClass = 'w-full px-4 py-3 bg-stone-50 dark:bg-[#3a3a3a] border border-stone-200 dark:border-[#555555] rounded-xl text-sm text-brand-charcoal dark:text-white focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange outline-none transition-all appearance-none';
  const tabs = [{
    id: 'quiz' as const,
    label: 'Material Quiz',
    icon: HelpCircle
  }, {
    id: 'calculator' as const,
    label: 'Calculator',
    icon: Calculator
  }];
  // Hand the quiz recommendation to the calculator: switch tabs and
  // pre-fill the material dropdown with the closest calcMaterials match.
  const handleCalculateFromQuiz = () => {
    if (!quizResult) return;
    const matchedId = quizResultToMaterialId(quizResult.name);
    setQuizMaterial(matchedId);
    setActiveTab('calculator');
  };
  // Send the calculated yardage back to the source (estimator or product).
  // Estimator: returns to homepage with ?qty=N&material=NAME&address=...
  //   so the Hero can pre-fill all three fields and scroll into view.
  // Product: returns to /shop/<slug>?qty=N — Product.tsx already handles
  //   the qty param and either snaps the dropdown or stores it as a custom
  //   estimatorQty that splits cleanly across loads.
  const handleApplyToSource = (yards: number, materialName: string) => {
    if (yards < 1) return;
    if (fromParam === 'product' && fromSlug) {
      navigate(`/shop/${fromSlug}?qty=${yards}`);
      return;
    }
    if (fromParam === 'estimator') {
      const params = new URLSearchParams();
      params.set('qty', String(yards));
      if (materialName) params.set('material', materialName);
      if (fromAddress) params.set('address', fromAddress);
      navigate(`/?${params.toString()}#estimator`);
      return;
    }
  };
  return <main className="overflow-x-hidden py-12 md:py-20 dark:bg-[#222222] min-h-[calc(100vh-64px)] transition-colors duration-300 bg-[#f2efe8]" data-id="element-3490" data-mp="1ioc48k6exy2x">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3491" data-mp="76t6h7blqnbr">
        {/* Header */}
        <div className="text-center mb-10" data-id="element-3492" data-mp="c6ozkbirgucj">
          <motion.h1 initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-3493" data-mp="c7qn3fgyr9b5">
            Help Me Choose
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-brand-muted dark:text-stone-300 text-sm md:text-base max-w-lg mx-auto" data-id="element-3494" data-mp="3fnw6v6e2e87p">
            Answer a few questions about your site and we'll name the material,
            the depth, and the amount — or run the numbers yourself.
          </motion.p>
        </div>

        {/* The project-guide grid lives on /projects, which is the canonical
            index for it — it carries the ItemList schema and the ranking URL.
            Duplicating it here gave two pages the same body, so this page is
            now just the quiz and the calculator, with one line pointing at
            the guides for anyone who can already name their job. */}
        <div className="mb-10 rounded-2xl border border-stone-200 bg-white p-5 dark:border-white/10 dark:bg-white/[0.04] sm:p-6" data-id="element-3495" data-mp="28uq9wows82iz">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3" data-id="element-3496" data-mp="2htlg40zttcma">
            <div className="min-w-0" data-id="element-3497" data-mp="2msetltf453mg">
              <h2 className="font-heading text-lg font-bold text-brand-charcoal dark:text-white" data-id="element-3498" data-mp="2mthjby0p900s">
                Already know the job?
              </h2>
              <p className="mt-1 text-sm text-brand-muted dark:text-stone-300" data-id="element-3499" data-mp="2rpop9qt3ibiq">
                The project guides list every layer, its depth, and the build
                order — plus the full material list.
              </p>
            </div>
            <Link to="/projects" className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-brand-orange px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-orange/90" data-id="element-3500" data-mp="35hux8pm3hc73">
              Browse project guides
              <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-3501" data-mp="3fhdhz89tx207" />
            </Link>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="text-center mb-4" data-id="element-3502" data-mp="omgfky2ex39y">
          <p className="text-sm text-brand-muted dark:text-stone-400" style={{
          fontSize: "18px",
          fontWeight: "900",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          color: "#17A347"
        }} data-id="element-3503" data-mp="oni0du98d44v">
            Not sure which project fits? Take the quiz or run the numbers.
          </p>
        </div>
        <div className="flex justify-center mb-12" data-id="element-3504" data-mp="13c4fbuuy3tvk">
          <div className="relative flex gap-8" data-id="element-3505" data-mp="17cev1z8jhynm">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex items-center pb-2 text-sm font-semibold transition-colors duration-300 ${isActive ? 'text-brand-charcoal dark:text-white' : 'text-brand-muted dark:text-stone-300 hover:text-brand-charcoal dark:hover:text-white'}`} data-id="element-3506" data-mp="tjynqm8lvegw">
                  <tab.icon className="w-4 h-4 mr-2" data-id="element-3507" data-mp="1r1yr093j3ce8" />
                  {tab.label}
                  {isActive && <motion.div className="absolute bottom-0 left-0 right-0 h-[3px] bg-brand-orange rounded-full" initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} data-id="element-3508" data-mp="20do82zyb50r1" />}
                </button>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto" data-id="element-3509" data-mp="1hgmzl55joiic">
          <AnimatePresence mode="wait" data-id="element-3510" data-mp="1df1fozulf3yn">
            {/* QUIZ */}
            {activeTab === 'quiz' && <motion.div key="quiz" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-3511" data-mp="1rej5nijmkn9l">
                <div className="bg-[#f5f5f5] dark:bg-[#474747] rounded-2xl border border-[#e5e5e5] dark:border-[#555555] shadow-sm overflow-hidden relative" data-id="element-3512" data-mp="32vj0aktg3e6s">
                  {/* Progress Bar */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-[#e5e5e5] dark:bg-[#555555]" data-id="element-3513" data-mp="35xnesffqsu6u">
                    <motion.div className="h-full bg-brand-orange" initial={{
                  width: 0
                }} animate={{
                  width: `${progressPercent}%`
                }} transition={{
                  duration: 0.5,
                  ease: 'easeInOut'
                }} data-id="element-3514" data-mp="39yqt2xi56i5y" />
                  </div>

                  <div className="p-8 md:p-12 bg-[#E8E4DB] dark:bg-[#282828]" data-id="element-3515" data-mp="2r5v9yxdkqyjd">
                    {quizResult ? <motion.div initial={{
                  opacity: 0,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.4,
                  ease: [0.16, 1, 0.3, 1]
                }} className="text-center relative" data-id="element-3516" data-mp="2w4zm9wvspcdx">
                        {/* Solid green badge — bolder anchor than the
                         previous stone-50 ring. Tightened the bottom
                         margin so the badge feels connected to the
                         "We Recommend" eyebrow it introduces. */}
                        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3517" data-mp="1qwwz6pog4xs6">
                          <CheckCircle2 className="w-6 h-6 text-green-700 dark:text-green-400" data-id="element-3518" data-mp="1hybm3a1quays" />
                        </div>
                        <h3 className="text-xs font-semibold tracking-widest uppercase text-brand-muted dark:text-stone-400 mb-2" data-id="element-3519" data-mp="1d5ihqna6t4n8">
                          We Recommend
                        </h3>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-4 tracking-tight" data-id="element-3520" data-mp="13agrfo46dwj2">
                          {quizResult.name}
                        </h2>
                        {/* Description darkened to stone-700 in light mode
                         for stronger body-copy contrast (~7:1 vs the
                         previous ~4.6:1 with stone-500). Dark-mode tone
                         unchanged — already readable. */}
                        <p className="text-stone-700 dark:text-stone-300 mb-6 max-w-md mx-auto leading-relaxed text-[15px]" data-id="element-3521" data-mp="1a61hzf9qjryb">
                          {quizResult.desc}
                        </p>

                        {/* Depth is the number people get wrong, and it drives
                            the whole order. Stating it here — before the
                            calculator — sets the expectation the calculator
                            then opens on. */}
                        {quizDepthGuide && <div className="mx-auto mb-10 max-w-md border-l-2 border-[#04A05C] pl-4 text-left" data-id="element-3522" data-mp="v9j3nl6c8ruz">
                            <p className="text-xs font-bold uppercase tracking-wider text-[#04A05C]" data-id="element-3523" data-mp="2lm8do2qu8xnx">
                              Recommended depth · {quizDepthGuide.depth}"
                            </p>
                            <p className="mt-1 text-sm leading-relaxed text-stone-600 dark:text-stone-400" data-id="element-3524" data-mp="dxm2jblplfe2">
                              {quizDepthGuide.note}
                            </p>
                          </div>}

                        {/* When a guide covers this job it beats the material
                            recommendation alone: it carries every layer, its
                            depth, and the build order. Promoted above the
                            buttons so it reads as the next step, not an aside. */}
                        {quizGuide && <Link to={`/projects/${quizGuide.slug}`} className="group mx-auto mb-8 flex max-w-md items-center gap-4 border border-[#04A05C]/30 bg-[#f2efe8] p-4 text-left transition-colors hover:border-[#04A05C] dark:border-[#04A05C]/40 dark:bg-[#04A05C]/10" data-id="element-3525" data-mp="16nb2rmqlhbem">
                            <quizGuide.icon className="h-6 w-6 shrink-0 text-[#04A05C]" aria-hidden="true" data-id="element-3526" data-mp="33gw42kik28qn" />
                            <span className="min-w-0 flex-1" data-id="element-3527" data-mp="17e128rckvxls">
                              <span className="block text-xs font-bold uppercase tracking-wider text-[#04A05C]" data-id="element-3528" data-mp="17fud6iu853zl">
                                Step-by-step guide
                              </span>
                              <span className="mt-0.5 block font-semibold text-brand-charcoal dark:text-white" data-id="element-3529" data-mp="1he1skx8v7dzh">
                                {quizGuide.name}
                              </span>
                              <span className="mt-0.5 block text-sm text-stone-600 dark:text-stone-400" data-id="element-3530" data-mp="1qaj4i2p1zfwa">
                                {quizGuide.materials.length} materials with depths · {quizGuide.typicalYards}
                              </span>
                            </span>
                            <ArrowRight className="h-4 w-4 shrink-0 text-[#04A05C] transition-transform group-hover:translate-x-0.5" aria-hidden="true" data-id="element-3531" data-mp="250gp68nrxngq" />
                          </Link>}

                        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-3532" data-mp="2e2hg6nsezd9y">
                          <button onClick={handleCalculateFromQuiz} className="inline-flex items-center justify-center px-6 py-2.5 bg-brand-orange text-white rounded-lg font-semibold text-sm hover:bg-brand-orange/90 transition-colors group" data-id="element-3533" data-mp="2fdypfbouojq">
                            <Calculator className="w-4 h-4 mr-2" data-id="element-3534" data-mp="3kk7gxlbwp9io" />
                            Calculate How Much I Need
                            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" data-id="element-3535" data-mp="l5lypok3cyvp" />
                          </button>
                          <Link to="/#materials" className="inline-flex items-center justify-center px-6 py-2.5 bg-white dark:bg-[#3a3a3a] border border-[#e5e5e5] dark:border-[#555555] text-brand-charcoal dark:text-white rounded-lg font-medium text-sm hover:bg-[#eeeeee] dark:hover:bg-[#4a4a4a] transition-colors" data-id="element-3536" data-mp="h4afc0rt99vy">
                            Browse Materials
                          </Link>
                          <button onClick={resetQuiz} className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg font-medium text-sm text-brand-muted dark:text-stone-300 hover:text-brand-charcoal dark:hover:text-white transition-colors" data-id="element-3537" data-mp="100s2e4vpsllv">
                            <RefreshCw className="w-4 h-4 mr-2" data-id="element-3538" data-mp="3umi7qxyzgn3y" />
                            Start Over
                          </button>
                        </div>
                      </motion.div> : currentQuizQuestion ? <AnimatePresence mode="wait" data-id="element-3539" data-mp="r4rluvbcq4qr">
                        <motion.div key={currentQuizQuestion.id} initial={{
                    opacity: 0,
                    x: 10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -10
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeOut'
                  }} data-id="element-3540" data-mp="r36t9b8lkvvj">
                          <h3 className="font-heading text-xl md:text-2xl font-semibold text-brand-charcoal dark:text-white mb-8 text-center tracking-tight" data-id="element-3541" data-mp="36wxe9l57e7gf">
                            {currentQuizQuestion.question}
                          </h3>
                          <div className="space-y-2.5 max-w-lg mx-auto" data-id="element-3542" data-mp="2udjxcrdrooda">
                            {currentQuizQuestion.options.map((option, index) => <motion.button key={option.value} initial={{
                        opacity: 0,
                        y: 5
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: index * 0.03
                      }} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full flex items-center justify-between p-4 rounded-xl border border-[#e5e5e5] dark:border-[#363636] dark:bg-[#252423] hover:border-brand-orange/50 dark:hover:border-brand-orange/50 hover:bg-[#f2efe8] dark:hover:bg-[#272625] transition-all group bg-[#f4f7f2]" data-id="element-3543" data-mp="34bzp8wkmc1nu" data-mpi={index}>
                                  <span className="font-medium text-[15px] text-brand-charcoal dark:text-stone-200 group-hover:text-brand-orange transition-colors" data-id="element-3544" data-mp="t82kn87p2mkg">
                                    {option.label}
                                  </span>
                                  <ChevronRight className="w-4 h-4 text-stone-300 dark:text-stone-400 group-hover:text-brand-orange transition-colors" data-id="element-3545" data-mp="135qtf224xulm" />
                                </motion.button>)}
                          </div>
                        </motion.div>
                      </AnimatePresence> : null}
                  </div>
                </div>
              </motion.div>}

            {/* CALCULATOR */}
            {activeTab === 'calculator' && <motion.div key="calculator" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} data-id="element-3546" data-mp="fh7linmzzju2">
                <MaterialCalculator variant="inline" initialMaterial={quizMaterial} initialProjectType={quizDepthGuide?.projectType ?? initialProjectType} initialDepth={quizDepthGuide?.depth} depthNote={quizDepthGuide?.note} onAddToCart={fromParam ? handleApplyToSource : undefined} data-id="element-3547" data-mp="1yfm0bsn0rv6f" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </main>;
}